import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '@utils/cn';

/**
 * Checkbox Root Component
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    /**
     * Indeterminate state for partial selection
     */
    indeterminate?: boolean;
  }
>(({ className, indeterminate, checked, ...props }, ref) => {
  // Use indeterminate state if provided
  const checkState = indeterminate ? 'indeterminate' : checked;

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        'peer h-5 w-5 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
        indeterminate && 'bg-primary text-primary-foreground',
        className
      )}
      checked={checkState}
      {...props}
    >
      <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
        {indeterminate ? <Minus className="h-4 w-4" /> : <Check className="h-4 w-4" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

/**
 * Checkbox Field Component (with Label)
 */
export interface CheckboxFieldProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  /**
   * Label text for the checkbox
   */
  label?: string;
  /**
   * Description text displayed below the label
   */
  description?: string;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Helper text to display
   */
  helperText?: string;
  /**
   * Wrapper div className
   */
  wrapperClassName?: string;
  /**
   * Label className
   */
  labelClassName?: string;
  /**
   * Indeterminate state for partial selection
   */
  indeterminate?: boolean;
  /**
   * Mark field as required. Displays asterisk next to label.
   */
  required?: boolean;
}

/**
 * CheckboxField Component
 *
 * A checkbox component with label, description, and error states.
 * Supports indeterminate state for partial selections.
 *
 * @example
 * // Basic checkbox
 * <CheckboxField label="Accept terms and conditions" />
 *
 * @example
 * // Checkbox with description
 * <CheckboxField
 *   label="Email notifications"
 *   description="Receive updates about your patients"
 * />
 *
 * @example
 * // Checkbox with error
 * <CheckboxField
 *   label="I agree"
 *   error="You must agree to continue"
 * />
 *
 * @example
 * // Indeterminate checkbox (for "select all" functionality)
 * <CheckboxField
 *   label="Select all patients"
 *   indeterminate={someSelected && !allSelected}
 *   checked={allSelected}
 * />
 *
 * @example
 * // With React Hook Form
 * const { register, formState: { errors } } = useForm();
 * <CheckboxField
 *   {...register('agreeToTerms', { required: 'You must agree' })}
 *   label="I agree to the terms"
 *   error={errors.agreeToTerms?.message}
 * />
 */
const CheckboxField = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxFieldProps
>(
  (
    {
      label,
      description,
      error,
      helperText,
      wrapperClassName,
      labelClassName,
      id,
      indeterminate,
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    const descriptionId = `${inputId}-description`;

    return (
      <div className={cn('flex flex-col gap-2', wrapperClassName)}>
        <div className="flex items-start gap-2">
          <Checkbox
            ref={ref}
            id={inputId}
            indeterminate={indeterminate}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={
              error ? errorId : description ? descriptionId : helperText ? helperId : undefined
            }
            aria-required={required}
            {...props}
          />
          <div className="grid gap-1.5 leading-none">
            {label && (
              <label
                htmlFor={inputId}
                className={cn(
                  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                  labelClassName
                )}
              >
                {label}
                {required && (
                  <span className="ml-1 text-xs text-destructive" aria-label="required">
                    *
                  </span>
                )}
              </label>
            )}
            {description && (
              <p id={descriptionId} className="text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        </div>
        {(error || helperText) && (
          <div
            id={error ? errorId : helperId}
            className={cn('text-sm', {
              'text-destructive': error,
              'text-muted-foreground': helperText && !error,
            })}
            role={error ? 'alert' : 'status'}
            aria-live="polite"
          >
            {error || helperText}
          </div>
        )}
      </div>
    );
  }
);

CheckboxField.displayName = 'CheckboxField';

/**
 * Checkbox Group Component
 *
 * A group of checkboxes with a common label
 */
export interface CheckboxGroupProps {
  /**
   * Label for the entire group
   */
  label?: string;
  /**
   * Description for the entire group
   */
  description?: string;
  /**
   * Error message for the group
   */
  error?: string;
  /**
   * Helper text for the group
   */
  helperText?: string;
  /**
   * Orientation of checkboxes
   */
  orientation?: 'vertical' | 'horizontal';
  /**
   * Wrapper className
   */
  className?: string;
  /**
   * Children (CheckboxField components)
   */
  children: React.ReactNode;
}

/**
 * CheckboxGroup Component
 *
 * A container for multiple related checkboxes.
 *
 * @example
 * <CheckboxGroup
 *   label="Symptoms"
 *   description="Select all that apply"
 *   orientation="vertical"
 * >
 *   <CheckboxField label="Fever" />
 *   <CheckboxField label="Cough" />
 *   <CheckboxField label="Fatigue" />
 * </CheckboxGroup>
 */
const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  label,
  description,
  error,
  helperText,
  orientation = 'vertical',
  className,
  children,
}) => {
  const id = React.useId();
  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;
  const descriptionId = `${id}-description`;

  return (
    <div className={cn('space-y-3', className)} role="group">
      {(label || description) && (
        <div className="space-y-1">
          {label && <div className="text-sm font-medium leading-none">{label}</div>}
          {description && (
            <p id={descriptionId} className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      )}
      <div
        className={cn('flex gap-4', orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap')}
      >
        {children}
      </div>
      {(error || helperText) && (
        <div
          id={error ? errorId : helperId}
          className={cn('text-sm', {
            'text-destructive': error,
            'text-muted-foreground': helperText && !error,
          })}
          role={error ? 'alert' : 'status'}
          aria-live="polite"
        >
          {error || helperText}
        </div>
      )}
    </div>
  );
};

export { Checkbox, CheckboxField, CheckboxGroup };
